import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { demoModeManager } from '../../../services/demoModeManager';
import {
  FiZap as Zap,
  FiPlus as Plus,
  FiTrash2 as Trash2,
  FiEdit2 as Edit2,
  FiPlay as Play,
  FiPause as Pause,
  FiRefreshCw as RefreshCw,
  FiChevronDown as ChevronDown,
  FiChevronRight as ChevronRight,
  FiCheckCircle as CheckCircle,
  FiXCircle as XCircle,
  FiClock as Clock,
  FiArrowLeft as ArrowLeft,
  FiActivity as Activity,
  FiDownload as Download,
  FiLoader as Loader
} from 'react-icons/fi';
import { IconType } from 'react-icons';

interface AutoActionsTabProps {
  apiBaseUrl: string;
  clientId: string;
}

interface PlaybookAction {
  type: string;
  to_version?: number;
  percentage?: number;
  message?: string;
  channel?: string;
}

interface PlaybookCondition {
  field: string;
  operator: string;
  value: string;
  metric?: string;
}

interface PlaybookTrigger {
  alert_metric?: string;
  severity?: string;
  condition?: string;
  threshold?: number;
}

interface Playbook {
  id: string;
  name: string;
  description?: string;
  is_enabled: boolean;
  is_builtin?: boolean;
  builtin_name?: string | null;
  execution_count?: number;
  cooldown_seconds?: number;
  max_executions_per_hour?: number;
  last_executed_at?: string | null;
  trigger?: PlaybookTrigger;
  conditions?: PlaybookCondition[];
  actions: PlaybookAction[];
}

interface PlaybookTemplate {
  name: string;
  display_name: string;
  description?: string;
  actions: PlaybookAction[];
}

interface ActionResult {
  action: string;
  status: string;
  details?: Record<string, unknown> | null;
  error?: string | null;
}

interface Execution {
  id: string;
  playbook_id: string;
  status: string;
  triggered_by?: string;
  started_at?: string;
  action_results?: ActionResult[];
}

interface PausedResource {
  id: string;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  paused_at?: string;
  paused_by_playbook_id?: string;
  pause_percentage?: number;
}

interface ActionTypeConfig {
  label: string;
  color: string;
  bg: string;
  icon: IconType;
}

interface StatusConfig {
  label: string;
  color: string;
  bg: string;
  icon: IconType;
}

interface PlaybookCardProps {
  playbook: Playbook;
  isRunning: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  onExecute: () => void;
}

interface ExecutionCardProps {
  execution: Execution;
}

interface PlaybookModalProps {
  playbook: Playbook | null;
  isSaving: boolean;
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

interface TemplatesModalProps {
  templates: PlaybookTemplate[];
  installedTemplates: string[];
  installingTemplate: string | null;
  onInstall: (templateName: string) => void;
  onClose: () => void;
}

// Action type display config
const ACTION_TYPES: Record<string, ActionTypeConfig> = {
  rollback: { label: 'Rollback', color: 'text-orange-400', bg: 'bg-orange-500/20', icon: ArrowLeft },
  pause_traffic: { label: 'Pause Traffic', color: 'text-red-400', bg: 'bg-red-500/20', icon: Pause },
  resume_traffic: { label: 'Resume Traffic', color: 'text-green-400', bg: 'bg-green-500/20', icon: Play },
  notify: { label: 'Notify', color: 'text-blue-400', bg: 'bg-blue-500/20', icon: Activity },
  webhook: { label: 'Webhook', color: 'text-purple-400', bg: 'bg-purple-500/20', icon: Zap }
};

// Execution status config
const STATUS_CONFIG: Record<string, StatusConfig> = {
  completed: { label: 'Completed', color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/20', icon: XCircle },
  running: { label: 'Running', color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: RefreshCw },
  cancelled: { label: 'Cancelled', color: 'text-gray-400', bg: 'bg-gray-500/20', icon: XCircle },
  pending: { label: 'Pending', color: 'text-blue-400', bg: 'bg-blue-500/20', icon: Clock }
};

const AutoActionsTab: React.FC<AutoActionsTabProps> = ({ apiBaseUrl, clientId }) => {
  // State
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [templates, setTemplates] = useState<PlaybookTemplate[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [pausedResources, setPausedResources] = useState<PausedResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState<Playbook | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('playbooks');

  // Loading states for operations
  const [runningPlaybookId, setRunningPlaybookId] = useState<string | null>(null);
  const [deletingPlaybookId, setDeletingPlaybookId] = useState<string | null>(null);
  const [savingPlaybook, setSavingPlaybook] = useState(false);
  const [installingTemplate, setInstallingTemplate] = useState<string | null>(null);
  const [resumingPauseId, setResumingPauseId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Demo mode: return mock data
      const { isDemoMode } = demoModeManager.getDemoMode();
      if (isDemoMode) {
        const now = new Date();
        setPlaybooks([
          {
            id: 'pbk000001',
            name: 'Auto-Rollback on Quality Drop',
            description: 'Automatically rolls back prompt to previous version when evaluation score drops below threshold',
            is_enabled: true,
            is_builtin: false,
            builtin_name: null,
            execution_count: 3,
            cooldown_seconds: 3600,
            max_executions_per_hour: 2,
            last_executed_at: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
            trigger: { alert_metric: 'evaluation_score', severity: 'high', condition: 'below_threshold', threshold: 0.75 },
            conditions: [{ field: 'environment', operator: 'equals', value: 'production' }],
            actions: [{ type: 'rollback' }, { type: 'notify' }]
          },
          {
            id: 'pbk000002',
            name: 'Cost Spike Circuit Breaker',
            description: 'Pauses traffic when cost per request exceeds budget threshold',
            is_enabled: true,
            is_builtin: false,
            builtin_name: null,
            execution_count: 1,
            cooldown_seconds: 1800,
            max_executions_per_hour: 3,
            last_executed_at: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
            trigger: { alert_metric: 'cost_per_request', severity: 'critical', condition: 'above_threshold', threshold: 0.05 },
            conditions: [],
            actions: [{ type: 'pause_traffic' }, { type: 'notify' }, { type: 'webhook' }]
          }
        ]);
        setTemplates([
          { name: 'auto-rollback', display_name: 'Auto-Rollback', description: 'Rollback to previous version on quality regression', actions: [{ type: 'rollback' }, { type: 'notify' }] },
          { name: 'circuit-breaker', display_name: 'Circuit Breaker', description: 'Pause traffic on critical alerts to limit blast radius', actions: [{ type: 'pause_traffic' }, { type: 'notify' }] },
          { name: 'alert-webhook', display_name: 'Alert Webhook', description: 'Send webhook notification on any alert', actions: [{ type: 'webhook' }] }
        ]);
        setExecutions([
          {
            id: 'exec000001',
            playbook_id: 'pbk000001',
            status: 'completed',
            triggered_by: 'alert: evaluation_score below 0.75',
            started_at: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
            action_results: [
              { action: 'rollback', status: 'success', details: { from_version: 2, to_version: 1, prompt: 'customer-support-classifier' }, error: null },
              { action: 'notify', status: 'success', details: { channel: 'slack', recipients: 3 }, error: null }
            ]
          },
          {
            id: 'exec000002',
            playbook_id: 'pbk000002',
            status: 'completed',
            triggered_by: 'alert: cost_per_request above $0.05',
            started_at: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
            action_results: [
              { action: 'pause_traffic', status: 'success', details: { resource: 'customer-support-classifier', pause_percentage: 50 }, error: null },
              { action: 'notify', status: 'success', details: { channel: 'email', recipients: 2 }, error: null },
              { action: 'webhook', status: 'failed', details: null, error: 'Connection timeout to https://hooks.example.com/alerts' }
            ]
          }
        ]);
        setPausedResources([]);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const headers: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [playbooksRes, templatesRes, executionsRes, pausesRes] = await Promise.all([
        fetch(`${apiBaseUrl}/v1/genaiops/pbks?client_id=${clientId}`, { headers }),
        fetch(`${apiBaseUrl}/v1/genaiops/pbks/templates/builtin`, { headers }),
        fetch(`${apiBaseUrl}/v1/genaiops/runs?client_id=${clientId}&limit=20`, { headers }),
        fetch(`${apiBaseUrl}/v1/genaiops/holds?client_id=${clientId}&is_active=true`, { headers })
      ]);

      if (playbooksRes.ok) setPlaybooks(await playbooksRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (executionsRes.ok) setExecutions(await executionsRes.json());
      if (pausesRes.ok) setPausedResources(await pausesRes.json());

      // Check if all requests failed
      if (!playbooksRes.ok && !templatesRes.ok && !executionsRes.ok && !pausesRes.ok) {
        throw new Error('Unable to connect to the Auto-Actions service. Please try again.');
      }
    } catch (err: unknown) {
      console.error('Error loading Auto-Actions data:', err);
      // Provide user-friendly error message
      let errorMessage = 'Unable to load Auto-Actions data.';
      const errMsg = err instanceof Error ? err.message : '';
      if (errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError')) {
        errorMessage = 'Unable to connect to the Auto-Actions service. Please check your connection and try again.';
      } else if (errMsg) {
        errorMessage = errMsg;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, clientId]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Playbook handlers
  const handleSavePlaybook = async (playbookData: Record<string, unknown>) => {
    setSavingPlaybook(true);
    try {
      const url = editingPlaybook
        ? `${apiBaseUrl}/v1/genaiops/pbks/${editingPlaybook.id}`
        : `${apiBaseUrl}/v1/genaiops/pbks`;

      const res = await fetch(url, {
        method: editingPlaybook ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...playbookData, client_id: clientId })
      });

      if (res.ok) {
        await fetchData();
        setShowPlaybookModal(false);
        setEditingPlaybook(null);
        toast.success(
          editingPlaybook ? 'Playbook updated successfully!' : 'Playbook created successfully!'
        );
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to save playbook');
      }
    } catch (err: unknown) {
      console.error('Failed to save playbook:', err);
      toast.error('Failed to save playbook');
    } finally {
      setSavingPlaybook(false);
    }
  };

  const handleDeletePlaybook = async (playbookId: string) => {
    if (!globalThis.confirm('Delete this playbook?')) return;

    setDeletingPlaybookId(playbookId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBaseUrl}/v1/genaiops/pbks/${playbookId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        await fetchData();
        toast.success('Playbook deleted');
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to delete playbook');
      }
    } catch (err: unknown) {
      console.error('Failed to delete playbook:', err);
      toast.error('Failed to delete playbook');
    } finally {
      setDeletingPlaybookId(null);
    }
  };

  const handleTogglePlaybook = async (playbook: Playbook) => {
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${apiBaseUrl}/v1/genaiops/pbks/${playbook.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_enabled: !playbook.is_enabled })
      });
      if (res.ok) {
        await fetchData();
        toast.success(playbook.is_enabled ? 'Playbook disabled' : 'Playbook enabled');
      } else {
        toast.error('Failed to toggle playbook');
      }
    } catch (err: unknown) {
      console.error('Failed to toggle playbook:', err);
      toast.error('Failed to toggle playbook');
    }
  };

  const handleExecutePlaybook = async (playbookId: string) => {
    setRunningPlaybookId(playbookId);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${apiBaseUrl}/v1/genaiops/pbks/${playbookId}/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ context: {} })
      });
      if (res.ok) {
        toast.success(
          <div>
            <strong>Playbook executed!</strong>
            <p className="text-sm opacity-80">Check the History tab for results.</p>
          </div>,
          { duration: 4000 }
        );
        await fetchData();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to execute playbook');
      }
    } catch (err: unknown) {
      console.error('Failed to execute playbook:', err);
      toast.error('Failed to execute playbook');
    } finally {
      setRunningPlaybookId(null);
    }
  };

  const handleInstallTemplate = async (templateName: string) => {
    setInstallingTemplate(templateName);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {};

      const res = await fetch(`${apiBaseUrl}/v1/genaiops/pbks/templates/${templateName}/install?client_id=${clientId}`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        await fetchData();
        setShowTemplatesModal(false);
        toast.success('Template installed successfully!');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to install template');
      }
    } catch (err: unknown) {
      console.error('Failed to install template:', err);
      toast.error('Failed to install template');
    } finally {
      setInstallingTemplate(null);
    }
  };

  const handleResumeResource = async (pauseId: string) => {
    setResumingPauseId(pauseId);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${apiBaseUrl}/v1/genaiops/holds/${pauseId}/resume`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'Manual resume from UI' })
      });
      if (res.ok) {
        await fetchData();
        toast.success('Resource resumed successfully');
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to resume resource');
      }
    } catch (err: unknown) {
      console.error('Failed to resume resource:', err);
      toast.error('Failed to resume resource');
    } finally {
      setResumingPauseId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
        <span className="ml-2 text-gray-400">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Auto-Actions
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Automated responses to alerts with rollbacks, circuit breakers, and notifications
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowTemplatesModal(true)}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            Templates
          </button>
          <button
            onClick={() => {
              setEditingPlaybook(null);
              setShowPlaybookModal(true);
            }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Playbook
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-red-400 mb-1">Failed to Load Auto-Actions</h4>
              <p className="text-red-400/80 text-sm mb-3">{error}</p>
              <button
                onClick={fetchData}
                disabled={loading}
                className="px-4 py-1.5 bg-red-500/30 hover:bg-red-500/40 text-red-300 rounded text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paused Resources Alert */}
      {pausedResources.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Pause className="w-5 h-5 text-yellow-400" />
            <span className="font-medium text-yellow-400">
              {pausedResources.length} Resource{pausedResources.length === 1 ? '' : 's'} Paused
            </span>
          </div>
          <div className="space-y-2">
            {pausedResources.map(pause => (
              <div key={pause.id} className="flex items-center justify-between bg-gray-900/50 rounded p-2">
                <div>
                  <span className="text-white">{pause.resource_type}: {pause.resource_id}</span>
                  <span className="text-gray-500 text-sm ml-2">({pause.pause_percentage}% paused)</span>
                </div>
                <button
                  onClick={() => handleResumeResource(pause.id)}
                  disabled={resumingPauseId === pause.id}
                  className={`px-3 py-1 rounded text-sm transition-colors flex items-center gap-1.5 ${
                    resumingPauseId === pause.id
                      ? 'bg-green-500/10 text-green-400/60 cursor-not-allowed'
                      : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                  }`}
                >
                  {resumingPauseId === pause.id ? (
                    <>
                      <Loader className="w-3 h-3 animate-spin" />
                      Resuming...
                    </>
                  ) : (
                    'Resume'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex gap-4">
          {[
            { id: 'playbooks', label: 'Actions', count: playbooks.length },
            { id: 'history', label: 'Execution History', count: executions.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              {tab.label}
              <span className="ml-2 text-xs text-gray-500">({tab.count})</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Playbooks Tab */}
      {activeTab === 'playbooks' && (
        <div className="space-y-4">
          {playbooks.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
              <Zap className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-medium text-white mb-2">No Actions Configured</h3>
              <p className="text-gray-400 mb-4">
                Create automated responses to handle alerts automatically
              </p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setShowTemplatesModal(true)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg inline-flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Install Template
                </button>
                <button
                  onClick={() => {
                    setEditingPlaybook(null);
                    setShowPlaybookModal(true);
                  }}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg inline-flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Custom
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {playbooks.map(playbook => (
                <PlaybookCard
                  key={playbook.id}
                  playbook={playbook}
                  isRunning={runningPlaybookId === playbook.id}
                  isDeleting={deletingPlaybookId === playbook.id}
                  onEdit={() => {
                    setEditingPlaybook(playbook);
                    setShowPlaybookModal(true);
                  }}
                  onDelete={() => handleDeletePlaybook(playbook.id)}
                  onToggle={() => handleTogglePlaybook(playbook)}
                  onExecute={() => handleExecutePlaybook(playbook.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {executions.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
              <Clock className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-medium text-white mb-2">No Execution History</h3>
              <p className="text-gray-400">
                Playbook executions will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {executions.map(execution => (
                <ExecutionCard key={execution.id} execution={execution} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showPlaybookModal && (
        <PlaybookModal
          playbook={editingPlaybook}
          isSaving={savingPlaybook}
          onSave={handleSavePlaybook}
          onClose={() => {
            setShowPlaybookModal(false);
            setEditingPlaybook(null);
          }}
        />
      )}

      {showTemplatesModal && (
        <TemplatesModal
          templates={templates}
          installedTemplates={playbooks.filter(p => p.is_builtin).map(p => p.builtin_name).filter((n): n is string => n != null)}
          installingTemplate={installingTemplate}
          onInstall={handleInstallTemplate}
          onClose={() => setShowTemplatesModal(false)}
        />
      )}
    </div>
  );
};

// Playbook Card Component
const PlaybookCard = ({ playbook, isRunning, isDeleting, onEdit, onDelete, onToggle, onExecute }: PlaybookCardProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`bg-gray-800/50 rounded-lg border ${playbook.is_enabled ? 'border-gray-700' : 'border-gray-800 opacity-60'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Toggle */}
            <button
              onClick={onToggle}
              className={`w-10 h-6 rounded-full transition-colors ${
                playbook.is_enabled ? 'bg-blue-500' : 'bg-gray-600'
              }`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                playbook.is_enabled ? 'translate-x-5' : 'translate-x-1'
              }`} />
            </button>

            {/* Info */}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-white">{playbook.name}</span>
                {playbook.is_builtin && (
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                    Built-in
                  </span>
                )}
              </div>
              {playbook.description && (
                <div className="text-sm text-gray-400 mt-1">{playbook.description}</div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {playbook.is_enabled && (
              <button
                onClick={onExecute}
                disabled={isRunning}
                className={`px-3 py-1.5 rounded flex items-center gap-1.5 text-sm transition-colors ${
                  isRunning
                    ? 'bg-green-500/10 text-green-400/60 cursor-not-allowed'
                    : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                }`}
              >
                {isRunning ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Run
                  </>
                )}
              </button>
            )}
            <button
              onClick={onEdit}
              className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className={`p-2 rounded transition-colors ${
                isDeleting
                  ? 'text-gray-500 cursor-not-allowed'
                  : 'hover:bg-gray-700 text-gray-400 hover:text-red-400'
              }`}
            >
              {isDeleting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-3 flex items-center gap-6 text-sm text-gray-500">
          <span>Executions: {playbook.execution_count || 0}</span>
          <span>Cooldown: {Math.floor((playbook.cooldown_seconds || 0) / 60)}min</span>
          <span>Max/hour: {playbook.max_executions_per_hour}</span>
          {playbook.last_executed_at && (
            <span>Last: {new Date(playbook.last_executed_at).toLocaleString()}</span>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-700 p-4 space-y-4">
          {/* Trigger */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Trigger</h4>
            <div className="bg-gray-900/50 rounded p-3 text-sm text-gray-400">
              <pre>{JSON.stringify(playbook.trigger, null, 2)}</pre>
            </div>
          </div>

          {/* Conditions */}
          {(playbook.conditions?.length ?? 0) > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Conditions</h4>
              <div className="space-y-1">
                {playbook.conditions!.map((cond: PlaybookCondition, i: number) => (
                  <div key={`${cond.metric}-${cond.operator}-${i}`} className="bg-gray-900/50 rounded p-2 text-sm text-gray-400">
                    {cond.metric} {cond.operator} {cond.value}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Actions</h4>
            <div className="space-y-2">
              {playbook.actions.map((action: PlaybookAction, i: number) => {
                const actionType = ACTION_TYPES[action.type] || ACTION_TYPES.webhook;
                const Icon = actionType.icon;
                return (
                  <div key={`${action.type}-${i}`} className={`rounded p-2 flex items-center gap-2 ${actionType.bg}`}>
                    <Icon className={`w-4 h-4 ${actionType.color}`} />
                    <span className={`text-sm ${actionType.color}`}>{actionType.label}</span>
                    <span className="text-gray-400 text-sm">
                      {action.to_version && `→ ${action.to_version}`}
                      {action.percentage && `${action.percentage}%`}
                      {action.message && `"${action.message}"`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Execution Card Component
const ExecutionCard = ({ execution }: ExecutionCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_CONFIG[execution.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg ${status.bg} flex items-center justify-center`}>
            <StatusIcon className={`w-4 h-4 ${status.color}`} />
          </div>
          <div>
            <div className="text-white">Playbook Execution</div>
            <div className="text-sm text-gray-400">
              Triggered by: {execution.triggered_by} • {execution.started_at ? new Date(execution.started_at).toLocaleString() : 'Unknown'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs ${status.bg} ${status.color}`}>
            {status.label}
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && execution.action_results && (
        <div className="mt-4 border-t border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Action Results</h4>
          <div className="space-y-2">
            {execution.action_results.map((result: ActionResult, i: number) => {
              const actionType = ACTION_TYPES[result.action] || ACTION_TYPES.webhook;
              const success = result.status === 'success';
              return (
                <div key={`${result.action}-${i}`} className={`rounded p-2 ${success ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  <div className="flex items-center gap-2">
                    {success ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className={success ? 'text-green-400' : 'text-red-400'}>
                      {actionType.label}
                    </span>
                  </div>
                  {result.details && (
                    <pre className="mt-2 text-xs text-gray-400 overflow-x-auto">
                      {JSON.stringify(result.details, null, 2)}
                    </pre>
                  )}
                  {result.error && (
                    <div className="mt-2 text-sm text-red-400">{result.error}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Playbook Modal Component (simplified for brevity)
const PlaybookModal = ({ playbook, isSaving, onSave, onClose }: PlaybookModalProps) => {
  const [formData, setFormData] = useState({
    name: playbook?.name || '',
    description: playbook?.description || '',
    trigger: playbook?.trigger || { alert_metric: 'error_rate', severity: 'critical' },
    conditions: playbook?.conditions || [],
    actions: playbook?.actions || [{ type: 'notify', channel: 'default', message: 'Alert triggered' }],
    cooldown_seconds: playbook?.cooldown_seconds || 3600,
    max_executions_per_hour: playbook?.max_executions_per_hour || 3,
    is_enabled: playbook?.is_enabled ?? true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData as unknown as Record<string, unknown>);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg w-full max-w-lg mx-4 border border-gray-700 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-700 sticky top-0 bg-gray-800">
          <h3 className="text-lg font-medium text-white">
            {playbook ? 'Edit Playbook' : 'Create Playbook'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Name</label>
            <input id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="cooldown-seconds" className="block text-sm font-medium text-gray-300 mb-1">Cooldown (seconds)</label>
              <input id="cooldown-seconds"
                type="number"
                value={formData.cooldown_seconds}
                onChange={(e) => setFormData({ ...formData, cooldown_seconds: Number.parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              />
            </div>
            <div>
              <label htmlFor="maxhour" className="block text-sm font-medium text-gray-300 mb-1">Max/hour</label>
              <input id="maxhour"
                type="number"
                value={formData.max_executions_per_hour}
                onChange={(e) => setFormData({ ...formData, max_executions_per_hour: Number.parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              />
            </div>
          </div>

          <div className="text-sm text-gray-400 bg-gray-900/50 rounded p-3">
            <p className="mb-2">For advanced trigger and action configuration, use the Templates or edit the JSON directly.</p>
            <pre className="text-xs overflow-x-auto">
              {JSON.stringify({ trigger: formData.trigger, actions: formData.actions }, null, 2)}
            </pre>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`px-4 py-2 rounded transition-colors flex items-center gap-2 ${
                isSaving
                  ? 'bg-blue-500/50 text-white/70 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                playbook ? 'Save Changes' : 'Create Playbook'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Templates Modal Component
const TemplatesModal = ({ templates, installedTemplates, installingTemplate, onInstall, onClose }: TemplatesModalProps) => {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg w-full max-w-2xl mx-4 border border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-400" />
              Built-in Templates
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Pre-configured actions for common scenarios
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {templates.map((template: PlaybookTemplate) => {
            const isInstalled = installedTemplates.includes(template.name);
            return (
              <div key={template.name} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-white">{template.display_name}</div>
                    <div className="text-sm text-gray-400 mt-1">{template.description}</div>
                    <div className="mt-2 flex gap-2">
                      {template.actions.map((action: PlaybookAction, i: number) => {
                        const actionType = ACTION_TYPES[action.type] || ACTION_TYPES.webhook;
                        return (
                          <span key={`${action.type}-${i}`} className={`px-2 py-0.5 rounded text-xs ${actionType.bg} ${actionType.color}`}>
                            {actionType.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  {isInstalled ? (
                    <span className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded text-sm">
                      Installed
                    </span>
                  ) : (
                    <button
                      onClick={() => onInstall(template.name)}
                      disabled={installingTemplate === template.name}
                      className={`px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-1.5 ${
                        installingTemplate === template.name
                          ? 'bg-blue-500/50 text-white/70 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                      }`}
                    >
                      {installingTemplate === template.name ? (
                        <>
                          <Loader className="w-3 h-3 animate-spin" />
                          Installing...
                        </>
                      ) : (
                        'Install'
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end p-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoActionsTab;
